import { LOCALE_CODES, Country } from '@global/types/types';

import { StripeElementLocale } from '@stripe/stripe-js/types/stripe-js/elements-group';
import { CheckoutLocale } from '@stripe/stripe-js/types/stripe-js/checkout';
import { interpolateString } from '@global/utils/StringService';
import { LOCALES_CONFIG } from '@global/App/constants/location/constants';
import { DEFAULT_LOCALE_CONFIG } from '@global/App/constants/location/configs';
import { SiteConfig } from '../SiteConfig';
import { CountryService } from '@global/utils/CountryService';

type BuildTitleParams = {
  name: string;
  locale: LOCALE_CODES;
  title: string;
};

export const buildTitleWithName = ({
  name,
  title,
  locale,
}: BuildTitleParams) => {
  const template =
    LOCALES_CONFIG[locale]?.templates?.titleWithName ||
    DEFAULT_LOCALE_CONFIG.templates.titleWithName;

  // template for de-de doesn't use name
  const nameString = name ? `${name}'s ` : '';

  return interpolateString(template, { name: nameString, title });
};

export const buildStringWithTitleAndVat = (
  locale: LOCALE_CODES,
  defaultTitle: string,
  step: number
) => {
  const vatKeyForTemplate = `vatStep${step}`;
  const currentConfig = LOCALES_CONFIG[locale];
  const vat = currentConfig?.VAT || DEFAULT_LOCALE_CONFIG.VAT;
  const template =
    currentConfig?.templates?.[vatKeyForTemplate] ||
    DEFAULT_LOCALE_CONFIG.templates[vatKeyForTemplate];

  const vatString = interpolateString(template, { vat: vat?.toString() });
  const vatTitle = LOCALES_CONFIG[locale].vatTitle || defaultTitle;
  return `${vatTitle} ${vatString}`;
};

export const makeBillingAddressString = (
  locale: LOCALE_CODES,
  data:
    | ({ zip: string; city: string; state?: string } & Record<string, string>)
    | null,
  country?: Country
): string | null => {
  if (!data) return null;
  const { zip, state, city } = data;

  const displayState =
    country && state
      ? CountryService.convertChinaStateCodeForDisplay(country, state)
      : state;

  const _state = displayState ? displayState + ' ' : '';

  const template =
    LOCALES_CONFIG[locale]?.templates?.billingAddress ||
    DEFAULT_LOCALE_CONFIG.templates.billingAddress;

  return interpolateString(template, { zip, city, state: _state });
};

export const setupHtmlLang = (locale: LOCALE_CODES) => {
  const attr = getLanguageByLocale(locale);
  document.documentElement.setAttribute('lang', attr);
};

export const countryCurrencySymbolByLocale = (locale: LOCALE_CODES) => {
  const defaultAttr = 'gb';
  return (parseLocale(locale)[1] || defaultAttr).toLowerCase() as
    | StripeElementLocale
    | CheckoutLocale;
};

export function getLanguageByLocale(locale: LOCALE_CODES) {
  return parseLocale(locale)[0];
}

export function parseLocale(locale: LOCALE_CODES) {
  return locale.split('-');
}

export function getFormattedLocale() {
  const { lang, iso } = SiteConfig.getConfig();
  return `${lang}-${iso?.toUpperCase()}`;
}

// TODO figure out how to move it to the locale configuration
export function getIsDeLocale(): boolean {
  const { locale } = SiteConfig.getConfig();
  const deLocales: LOCALE_CODES[] = ['de-de', 'en-de', 'en-au'];
  return deLocales.includes(locale);
}

export function getIsDeOrAu(code: string): boolean {
  const countryCode = ['DE', 'AU'];
  return countryCode.includes(code);
}

/**
 * Build a locale to pass to a WPS component.
 * It supports all locales that are support in Stripe.
 * We pass `en` or `de` based on the language in the url, and add GB in case if a country is GB.
 * @see https://docs.stripe.com/js/appendix/supported_locales
 */
export const buildStripeLocale = (): string => {
  const { lang, iso } = SiteConfig.getConfig();
  if (iso === 'gb') {
    return `${lang}-${iso.toUpperCase()}}`;
  }
  return lang;
};
